// utils/api/DocumentEditorApi/AIApi.js

import { apiCall } from '../ApiUtils';

// Document Editor specific API call function
const documentEditorApiCall = (endpoint, options = {}, classContext = null) => {
  const documentEditorEndpoint = `/document-editor${endpoint}`;
  return apiCall(documentEditorEndpoint, options, classContext);
};

export const aiApi = {
  // Context-aware Document Editor AI Function
  unifiedAI: (message, context = {}, classContext = null) =>
    documentEditorApiCall('/ai', {
      method: 'POST',
      body: JSON.stringify({
        message,
        context
      })
    }, classContext),

  // Legacy support - converts old parameters to new context format
  unifiedAILegacy: (message, documentContent = '', selectedText = '', author = '', classContext = null, isNewDocument = false) =>
    documentEditorApiCall('/ai', {
      method: 'POST',
      body: JSON.stringify({
        message,
        context: {
          requestSource: 'chat',
          documentState: {
            content: documentContent,
            title: '',
            hasContent: Boolean(documentContent.trim()),
            hasTitle: false,
            questions: []
          },
          selectedText,
          author
        }
      })
    }, classContext),

  // Advanced Document Assistant Functions (mirrored from lms-system)
  summarizeText: (text, classContext = null) =>
    documentEditorApiCall('/summarize-text', {
      method: 'POST',
      body: JSON.stringify({
        text
      })
    }, classContext),

  processDiscussion: (text, summary, question, classContext = null) =>
    documentEditorApiCall('/process-discussion', {
      method: 'POST',
      body: JSON.stringify({
        text,
        summary,
        question
      })
    }, classContext),

  lookupPapers: (topic, classContext = null) =>
    documentEditorApiCall('/lookup-papers', {
      method: 'POST',
      body: JSON.stringify({
        topic
      })
    }, classContext),

  expandAbbreviations: (topic, classContext = null) =>
    documentEditorApiCall('/expand-abbreviations', {
      method: 'POST',
      body: JSON.stringify({
        topic
      })
    }, classContext),

  getEmbeddings: (text, classContext = null) =>
    documentEditorApiCall('/get-embeddings', {
      method: 'POST',
      body: JSON.stringify({
        text
      })
    }, classContext),

  processPdf: (url, classContext = null) =>
    documentEditorApiCall('/process-pdf', {
      method: 'POST',
      body: JSON.stringify({
        url
      })
    }, classContext),

  // Q&A Management Functions
  createQA: (documentId, question, documentText, documentSummary, isAdditional = false, classContext = null, environmentId = null) =>
    documentEditorApiCall('/qa/create', {
      method: 'POST',
      body: JSON.stringify({
        documentId,
        question,
        documentText,
        documentSummary,
        isAdditional,
        environmentId,
      })
    }, classContext),

  saveGeneratedQuestions: (documentId, questions, classContext = null) =>
    documentEditorApiCall('/qa/save-generated', {
      method: 'POST',
      body: JSON.stringify({
        documentId,
        questions
      })
    }, classContext),

  getQAList: (documentId, classContext = null) =>
    documentEditorApiCall(`/qa/list/${documentId}`, {
      method: 'GET'
    }, classContext),

  // Additional Q&A Functions
  getAdditionalQAList: (documentId, classContext = null) =>
    documentEditorApiCall(`/qa/additional/list/${documentId}`, {
      method: 'GET'
    }, classContext),

  promoteAdditionalQA: (qaId, classContext = null) =>
    documentEditorApiCall(`/qa/promote/${qaId}`, {
      method: 'POST'
    }, classContext),

  updateQA: (qaId, rephrased_question, answer, visible, classContext = null) =>
    documentEditorApiCall(`/qa/update/${qaId}`, {
      method: 'PUT',
      body: JSON.stringify({
        rephrased_question,
        answer,
        visible
      })
    }, classContext),

  deleteQA: (qaId, classContext = null) =>
    documentEditorApiCall(`/qa/delete/${qaId}`, {
      method: 'DELETE'
    }, classContext),

  toggleQAVisibility: (qaId, visible, classContext = null) =>
    documentEditorApiCall(`/qa/toggle-visibility/${qaId}`, {
      method: 'POST',
      body: JSON.stringify({
        visible
      })
    }, classContext),

  // Submit question from highlighted text in preview mode
  submitQuestion: ({ document_id, question, context, class_id }) =>
    documentEditorApiCall('/qa/submit-from-highlight', {
      method: 'POST',
      body: JSON.stringify({
        document_id,
        question,
        context,
        class_id
      })
    }, { classId: class_id }),
};